import React from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { FeatureFlagNames, useFeatureFlags } from "@/providers/featureFlags";
import { ExpandLess20, ExpandMore20 } from "@/components/ui/SvgIcon";

import { useCurrentUserQuery } from "@/features/auth/queries/useCurrentUserQuery";
import { useCanAccessUsersManagementPage } from "@/features/system/hooks/useUserManagementPermissions";
import { UserRoleKey } from "@/features/system/types";
import { useTripViewSidebarTargetPath } from "@/features/towers/hooks/useTripViewSidebarTargetPath";
import { hasRequiredRole } from "@/features/utils";

import cn from "@/utils/cn";

import { MainNavChildConfig, MainNavItemConfig } from "../config/mainNavConfig";

import { SidebarNavCollapsedFlyout } from "./SidebarNavCollapsedFlyout";
import { SidebarNavItemLink } from "./SidebarNavItemLink";
import { SidebarNavNestedLink } from "./SidebarNavNestedLink";

type Props = {
  item: MainNavItemConfig;
  pathname: string;
  isCollapsed: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
};

const isChildVisible = (
  child: MainNavChildConfig,
  isEnabled: (flag: FeatureFlagNames) => boolean,
  userRole: UserRoleKey | "ADMIN" | undefined,
  canAccessUsersManagement: boolean,
) => {
  if (child.id === "system-users" && !canAccessUsersManagement) {
    return false;
  }

  if (
    child.featureFlags &&
    !child.featureFlags.some((flag) => isEnabled(flag))
  ) {
    return false;
  }

  if (child.featureFlag && !isEnabled(child.featureFlag)) {
    return false;
  }

  if (child.hideWhenAnyFeatureFlags?.some((flag) => isEnabled(flag))) {
    return false;
  }

  if (child.requiredRole) {
    if (!hasRequiredRole(userRole, child.requiredRole)) {
      return false;
    }
  }

  return true;
};

const isParentVisible = (
  item: MainNavItemConfig,
  isEnabled: (flag: FeatureFlagNames) => boolean,
) => {
  if (item.featureFlags && !item.featureFlags.some((flag) => isEnabled(flag))) {
    return false;
  }

  if (item.featureFlag && !isEnabled(item.featureFlag)) {
    return false;
  }

  return true;
};

export const SidebarNavParentItem = ({
  item,
  pathname,
  isCollapsed,
  isExpanded,
  onToggleExpanded,
}: Props) => {
  const intl = useIntl();
  const { isEnabled } = useFeatureFlags();
  const { data: currentUserData } = useCurrentUserQuery();
  const canAccessUsersManagement = useCanAccessUsersManagementPage();
  const tripViewSidebarTargetPath = useTripViewSidebarTargetPath();

  if (!isParentVisible(item, isEnabled)) {
    return null;
  }

  const visibleChildren =
    item.children?.filter((child) =>
      isChildVisible(
        child,
        isEnabled,
        currentUserData?.user.role,
        canAccessUsersManagement,
      ),
    ) ?? [];

  const resolveChildTo = (child: MainNavChildConfig) =>
    child.id === "towers-trip-view" ? tripViewSidebarTargetPath : child.to;

  const showSubpagesInSideMenu = isEnabled(
    FeatureFlagNames.ShowSubpagesInSideMenu,
  );
  const nestedChildren = showSubpagesInSideMenu ? visibleChildren : [];
  const hasChildren = nestedChildren.length > 0;
  const isParentActive =
    item.isActive?.(pathname) ?? pathname.startsWith(item.to);
  const isChildActive = nestedChildren.some((child) =>
    child.isActive ? child.isActive(pathname) : pathname.startsWith(child.to),
  );
  const isParentHighlighted = isParentActive && !isChildActive;
  const isSectionActive = isParentActive || isChildActive;
  const isCurrent = hasChildren ? isParentHighlighted : isParentActive;
  const label = intl.formatMessage(item.title);
  const iconElement = <item.icon />;
  const shouldWrapLabel = item.id === "towers";

  if (isCollapsed) {
    if (hasChildren) {
      return (
        <SidebarNavCollapsedFlyout
          to={item.to}
          name={label}
          icon={iconElement}
          isCurrent={isCurrent}
          isSectionActive={isSectionActive}
          flyoutItems={nestedChildren.map((child) => {
            const childTo = resolveChildTo(child);
            return {
              child: { ...child, to: childTo },
              label: intl.formatMessage(child.title),
              isActive: child.isActive
                ? child.isActive(pathname)
                : pathname.startsWith(childTo),
            };
          })}
        />
      );
    }

    return (
      <SidebarNavItemLink
        to={item.to}
        name={label}
        icon={iconElement}
        isCurrent={isCurrent}
        isCollapsed={isCollapsed}
      />
    );
  }

  if (!hasChildren) {
    return (
      <SidebarNavItemLink
        to={item.to}
        name={label}
        icon={iconElement}
        isCurrent={isCurrent}
        isCollapsed={isCollapsed}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-0.5">
      <div
        className={cn(
          "flex min-h-12 items-center rounded-md p-3 transition-all duration-300 hover:bg-chorus-black-200",
          isParentHighlighted && "bg-chorus-black-200",
        )}
      >
        <Link
          to={item.to}
          className={cn(
            "flex min-w-0 flex-1 items-center gap-x-[10px] text-sm leading-4",
            isSectionActive
              ? "font-semibold text-chorus-gray-200"
              : "font-medium text-chorus-gray-300",
          )}
          data-testid={label}
        >
          {React.cloneElement(iconElement, {
            className: cn(
              "size-6 shrink-0 self-center",
              isSectionActive ? "fill-chorus-gray-400" : "fill-chorus-gray-600",
            ),
          })}
          <span
            className={cn(
              "min-w-0 flex-1",
              shouldWrapLabel ? "break-words" : "whitespace-nowrap",
            )}
          >
            {label}
          </span>
        </Link>
        <button
          type="button"
          onClick={onToggleExpanded}
          className="ml-1 flex size-5 shrink-0 items-center justify-center rounded-md text-chorus-gray-600 hover:text-chorus-gray-400"
          aria-expanded={isExpanded}
          aria-label={
            isExpanded
              ? intl.formatMessage({
                  defaultMessage: "Collapse section",
                  description:
                    "Aria label for button that collapses a nested sidebar section.",
                })
              : intl.formatMessage({
                  defaultMessage: "Expand section",
                  description:
                    "Aria label for button that expands a nested sidebar section.",
                })
          }
        >
          {isExpanded ? (
            <ExpandLess20 className="size-5 fill-current" />
          ) : (
            <ExpandMore20 className="size-5 fill-current" />
          )}
        </button>
      </div>
      {isExpanded &&
        nestedChildren.map((child: MainNavChildConfig) => {
          const childTo = resolveChildTo(child);
          const childLabel = intl.formatMessage(child.title);
          const childActive = child.isActive
            ? child.isActive(pathname)
            : pathname.startsWith(childTo);

          return (
            <SidebarNavNestedLink
              key={child.id}
              to={childTo}
              name={childLabel}
              icon={child.icon ? <child.icon /> : undefined}
              isCurrent={childActive}
            />
          );
        })}
    </div>
  );
};
